import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, X, CheckCircle, BookOpen, Volume2, Lightbulb, ChefHat } from 'lucide-react';
import { useSound } from '../hooks/useSound';
import { useS3Audio } from '../hooks/useS3Audio';

/* ═══════════════════════════════════════════
   HELPER: Get correct answer for Horloge items
   ═══════════════════════════════════════════ */
function getHorlogeCorrectAnswer(item) {
  const m = item.mode;
  if (m === 'see_produce_french' || m === 'see_clock_produce_french' || m === 'read_set_clock') return item.french_word || item.time_french;
  if (m === 'hear_type_number' || m === 'math_in_french') return item.number != null ? String(item.number) : item.french_word;
  if (m === 'time_reading') return item.time_french;
  if (m === 'hear_type_time' || m === 'hear_produce_time') return item.time_display || item.time_french;
  if (m === 'time_with_future') return item.time_french || item.time_display;
  return item.french_word || item.time_french || String(item.number ?? '');
}

function getHorlogePrompt(item) {
  const m = item.mode;
  if (m === 'see_produce_french') return item.number != null ? String(item.number) : null;
  if (m === 'math_in_french') return item.math_display || item.math_display_numeric || null;
  if (m === 'read_set_clock') return item.french_word || item.time_french || null;
  if (m === 'time_with_future') return item.sentence_fr || null;
  return null;
}

function getHorlogeInstruction(item) {
  const m = item.mode;
  if (m === 'see_produce_french') return item.number != null ? `Write ${item.number} in French!` : 'Write this in French!';
  if (m === 'hear_type_number') return 'Listen! Type the number';
  if (m === 'math_in_french') return 'Read the French equation and solve!';
  if (m === 'see_clock_produce_french' || m === 'time_reading') return 'Read the clock! Write the time in French';
  if (m === 'hear_produce_time' || m === 'hear_type_time') return 'Listen! Type the time';
  if (m === 'read_set_clock') return 'Read the French, what time is it?';
  if (m === 'time_with_future') return 'What time is mentioned?';
  return 'Type your answer!';
}

/* ═══════════════════════════════════════════
   SORT TYPE for Tri Express
   ═══════════════════════════════════════════ */
const CAT_COLORS_REVIEW = ['#E03131', '#1971C2', '#F59F00', '#2F9E44'];

/* ═══════════════════════════════════════════
   MATCH TYPE for Memoire
   ═══════════════════════════════════════════ */
const MATCH_TYPE_LABELS = {
  french_english: 'French = English', infinitive_conjugated: 'Infinitif = Conjugated',
  infinitive_past_participle: 'Infinitif = Participe', french_french: 'French = French',
  masculine_feminine: 'Masculin = Feminin', question_answer: 'Question = Answer',
  antonym_pairs: 'Opposites', antonym: 'Opposites', verb_conjugation: 'Conjugation',
  subject_agreement: 'Subject = Agreement', sentence_halves: 'Start = End',
  pronoun_verb_form: 'Pronoun = Verb', pronoun_avoir: 'Pronoun = Avoir',
  profession_workplace: 'Profession = Place', present_future: 'Present = Future',
  auxiliary_verb: 'Auxiliary = Verb', affirmative_negative: 'Affirm = Negative',
};

/* ═══════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════ */
export function ReviewPlayer({ items, settings, gameName, onExit }) {
  const [currentIdx, setCurrentIdx] = useState(0);
  const { playGameMusic, stopMusic } = useSound();
  const { play: playS3Audio, stop: stopS3Audio } = useS3Audio();

  // Play calm music during review
  useEffect(() => { playGameMusic('endscreen'); return () => stopMusic(); }, [playGameMusic, stopMusic]);

  // Prepare slides based on game type
  const slides = useMemo(() => {
    return buildSlides(items, gameName, settings);
  }, [items, gameName, settings]);

  const total = slides.length;

  const prev = useCallback(() => { stopS3Audio(); setCurrentIdx(i => Math.max(0, i - 1)); }, [stopS3Audio]);
  const next = useCallback(() => { stopS3Audio(); setCurrentIdx(i => Math.min(total - 1, i + 1)); }, [total, stopS3Audio]);

  // Keyboard navigation: arrow keys + Escape
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); next(); }
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); prev(); }
      else if (e.key === 'Escape') { e.preventDefault(); onExit(); }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [next, prev, onExit]);

  if (total === 0) return null;

  const slide = slides[currentIdx];

  return (
    <div data-testid="review-player" style={{
      height: '100vh', display: 'flex', flexDirection: 'column',
      background: 'linear-gradient(180deg, #F8F9FA 0%, #E8EDF4 100%)',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0.75rem 1.5rem', background: 'rgba(255,255,255,0.95)',
        borderBottom: '2px solid rgba(0,0,0,0.06)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <BookOpen size={24} color="var(--primary)" />
          <span style={{ fontFamily: 'Fredoka', fontSize: '1.3rem', fontWeight: 700, color: 'var(--text-primary)' }}>
            Review with Answers
          </span>
          <span style={{
            padding: '0.15rem 0.6rem', borderRadius: '2rem',
            background: 'rgba(47,158,68,0.1)', fontFamily: 'Fredoka',
            fontSize: '0.85rem', fontWeight: 700, color: 'var(--success)',
          }}>
            Correct answers shown
          </span>
        </div>
        <button data-testid="review-exit-btn" onClick={() => { stopS3Audio(); onExit(); }} style={{
          display: 'flex', alignItems: 'center', gap: '0.4rem',
          padding: '0.5rem 1.25rem', borderRadius: '0.75rem',
          background: 'white', border: '2px solid var(--border)',
          borderBottom: '4px solid var(--border)', cursor: 'pointer',
          fontFamily: 'Fredoka', fontSize: '1rem', fontWeight: 700,
          color: 'var(--text-secondary)',
        }}>
          <X size={18} /> Exit Review
        </button>
      </div>

      {/* Slide content */}
      <div style={{ flex: 1, overflow: 'auto', display: 'flex', justifyContent: 'center', padding: '1.5rem' }}>
        <div style={{ maxWidth: 1000, width: '100%' }}>
          {slide.type === 'mcq' && <MCQSlide slide={slide} playS3Audio={playS3Audio} gameName={gameName} />}
          {slide.type === 'team_mcq' && <TeamMCQSlide slide={slide} settings={settings} />}
          {slide.type === 'text_answer' && <TextAnswerSlide slide={slide} />}
          {slide.type === 'category' && <CategorySlide slide={slide} />}
          {slide.type === 'pairs' && <PairsSlide slide={slide} />}
          {slide.type === 'sentence' && <SentenceSlide slide={slide} />}
          {slide.type === 'passage' && <PassageSlide slide={slide} />}
          {slide.type === 'recipe_step' && <RecipeStepSlide slide={slide} />}
        </div>
      </div>

      {/* Navigation */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1.5rem',
        padding: '1rem 1.5rem', background: 'rgba(255,255,255,0.95)',
        borderTop: '2px solid rgba(0,0,0,0.06)',
        boxShadow: '0 -2px 8px rgba(0,0,0,0.04)',
      }}>
        <button data-testid="review-prev-btn" onClick={prev} disabled={currentIdx === 0}
          style={navBtnStyle(currentIdx === 0)}>
          <ChevronLeft size={24} /> Previous
        </button>

        <div style={{
          fontFamily: 'Fredoka', fontSize: '1.4rem', fontWeight: 700,
          color: 'var(--text-primary)', minWidth: 100, textAlign: 'center',
        }}>
          {currentIdx + 1} / {total}
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontWeight: 600, marginTop: '0.1rem' }}>
            Use arrow keys
          </div>
        </div>

        <button data-testid="review-next-btn" onClick={next} disabled={currentIdx === total - 1}
          style={navBtnStyle(currentIdx === total - 1)}>
          Next <ChevronRight size={24} />
        </button>
      </div>
    </div>
  );
}

const navBtnStyle = (disabled) => ({
  display: 'flex', alignItems: 'center', gap: '0.4rem',
  padding: '0.75rem 2rem', borderRadius: '1rem',
  background: disabled ? 'rgba(0,0,0,0.04)' : 'var(--primary)',
  color: disabled ? 'var(--text-muted)' : 'white',
  border: 'none', borderBottom: disabled ? '4px solid rgba(0,0,0,0.06)' : '4px solid #3B5BDB',
  fontFamily: 'Fredoka', fontSize: '1.2rem', fontWeight: 700,
  cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1,
  minHeight: 56, touchAction: 'manipulation',
  boxShadow: disabled ? 'none' : '0 4px 16px rgba(76,110,245,0.2)',
});

/* ═══════════════════════════════════════════
   BUILD SLIDES from raw items
   ═══════════════════════════════════════════ */
function buildSlides(items, gameName, settings) {
  const isTeam = ['la_course', 'duel_conjugaison'].includes(gameName);
  const validMCQ = items.filter(i => i.options && i.options.length > 0);

  if (isTeam) {
    const t1Items = validMCQ.filter((_, i) => i % 2 === 0);
    const t2Items = validMCQ.filter((_, i) => i % 2 === 1);
    const maxLen = Math.max(t1Items.length, t2Items.length);
    const target = Math.min(settings.target || 10, t1Items.length, t2Items.length);
    return Array.from({ length: Math.min(maxLen, target + 2) }).map((_, i) => ({
      type: 'team_mcq', t1Item: t1Items[i] || null, t2Item: t2Items[i] || null,
      slideNum: i + 1,
    }));
  }

  if (gameName === 'horloge_folle') {
    const maxRounds = Math.min(settings.rounds || 999, items.length);
    return items.slice(0, maxRounds).map((item, i) => ({
      type: 'text_answer', item, slideNum: i + 1,
      instruction: getHorlogeInstruction(item),
      prompt: getHorlogePrompt(item),
      correctAnswer: getHorlogeCorrectAnswer(item),
      audioText: item.audio_text_fr,
    }));
  }

  if (gameName === 'avalanche') {
    const avalItems = items.filter(i => i.correct_words || i.distractor_words);
    const maxSets = Math.min(settings.rounds || 10, avalItems.length);
    return avalItems.slice(0, maxSets).map((item, i) => ({
      type: 'category', slideNum: i + 1,
      title: item.target_category_fr || '',
      instruction: item.target_instruction || '',
      words: [
        ...(item.correct_words || []).map(w => ({ text: w, isCorrect: true })),
        ...(item.distractor_words || []).map(w => ({ text: w, isCorrect: false })),
      ],
    }));
  }

  if (gameName === 'memoire') {
    const memoItems = items.filter(i => i.pairs && i.pairs.length > 0);
    const maxRounds = Math.min(settings.rounds || 10, memoItems.length);
    return memoItems.slice(0, maxRounds).map((item, i) => ({
      type: 'pairs', slideNum: i + 1,
      title: item.title || 'Memory Match',
      matchType: MATCH_TYPE_LABELS[item.match_type] || item.match_type || 'Matching',
      pairs: (item.pairs || []).map(p => ({ a: p.card_a, b: p.card_b })),
    }));
  }

  if (gameName === 'tri_express') {
    const triItems = items.filter(i => i.categories && i.items);
    const maxSets = Math.min(settings.rounds || 10, triItems.length);
    return triItems.slice(0, maxSets).map((item, i) => ({
      type: 'category', slideNum: i + 1,
      title: item.title || item.title_en || '',
      categories: (item.categories || []).map((c, ci) => ({ label: c.label, color: CAT_COLORS_REVIEW[ci % CAT_COLORS_REVIEW.length] })),
      words: (item.items || []).map(w => ({
        text: w.display || w.text,
        isCorrect: true, // all words are valid, just need correct category
        correctCategory: (item.categories || []).find(c => c.index === w.category)?.label || '?',
      })),
    }));
  }

  if (gameName === 'batisseur') {
    const batItems = items.filter(i => i.scrambled_words && i.correct_order);
    const maxRounds = Math.min(settings.rounds || 10, batItems.length);
    return batItems.slice(0, maxRounds).map((item, i) => ({
      type: 'sentence', slideNum: i + 1,
      english: item.english_meaning || '',
      correctOrder: item.correct_order || [],
      hint: item.hint,
      completedSentence: item.completed_sentence,
    }));
  }

  if (gameName === 'robot_balayeur') {
    const robotItems = items.filter(i => i.words && i.words.length > 0);
    const maxSets = Math.min(settings.rounds || 999, robotItems.length);
    return robotItems.slice(0, maxSets).map((item, i) => ({
      type: 'passage', slideNum: i + 1,
      title: item.title || item.title_en || 'Passage',
      titleEn: item.title_en,
      words: item.words || [],
      contentType: item.content_type,
    }));
  }

  if (gameName === 'le_chef') {
    const chefItems = items.filter(i => i.steps && i.steps.length > 0);
    const maxRecipes = Math.min(settings.rounds || 999, chefItems.length);
    const slides = [];
    chefItems.slice(0, maxRecipes).forEach((recipe, ri) => {
      (recipe.steps || []).forEach((step, si) => {
        slides.push({
          type: 'recipe_step',
          slideNum: slides.length + 1,
          recipeName: recipe.recipe_name_fr,
          recipeNameEn: recipe.recipe_name_en,
          culturalOrigin: recipe.cultural_origin,
          ingredients: recipe.ingredients_needed,
          stepNum: si + 1,
          totalSteps: recipe.steps.length,
          recipeNum: ri + 1,
          totalRecipes: maxRecipes,
          instructionFr: step.instruction_fr,
          instructionEn: step.instruction_en,
          correctAction: step.correct_action,
          distractors: step.distractors || [],
        });
      });
    });
    return slides;
  }

  // Default MCQ (La Tour, Le Detective, Carte du Canada)
  const maxRounds = Math.min(settings.rounds || settings.target || 999, validMCQ.length);
  return validMCQ.slice(0, maxRounds).map((item, i) => ({
    type: 'mcq', item, slideNum: i + 1,
  }));
}

/* ═══════════════════════════════════════════
   MCQ SLIDE — Solo Q&A games
   ═══════════════════════════════════════════ */
function MCQSlide({ slide, playS3Audio, gameName }) {
  const { item } = slide;
  const correctIdx = (item.options || []).findIndex(o => o.correct);

  const handlePlayAudio = () => {
    if (playS3Audio && item.item_id && item.audio_text_fr) {
      playS3Audio(gameName || item.game || 'detective', item.item_id, item.audio_text_fr);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.25rem' }}>
      {/* Question */}
      <div style={{
        background: 'rgba(255,255,255,0.95)', borderRadius: '1.5rem', padding: '1.5rem 2rem',
        border: '2px solid rgba(0,0,0,0.06)', width: '100%', maxWidth: 700,
        boxShadow: '0 4px 20px rgba(0,0,0,0.06)', textAlign: 'center',
      }}>
        {item.prompt_image && (
          <img src={process.env.REACT_APP_IMAGE_BASE_URL ? `${process.env.REACT_APP_IMAGE_BASE_URL}/${item.prompt_image}` : `/assets/content/${item.prompt_image}`} alt="" style={{
            maxWidth: 250, maxHeight: 180, objectFit: 'contain', margin: '0 auto 0.75rem', display: 'block', borderRadius: '0.75rem',
          }} onError={e => { e.target.style.display = 'none'; }} />
        )}
        {item.audio_text_fr && !item.prompt_text_fr && (
          <div onClick={handlePlayAudio} data-testid="review-play-audio" style={{
            fontFamily: 'Fredoka', fontSize: '1.4rem', fontWeight: 600, color: 'var(--text-primary)',
            fontStyle: 'italic', lineHeight: 1.4, marginBottom: '0.5rem',
            cursor: 'pointer',
          }}>
            <Volume2 size={20} style={{ verticalAlign: 'middle', marginRight: '0.4rem' }} color="#4C6EF5" />
            "{item.audio_text_fr}"
          </div>
        )}
        {item.prompt_text_en && (
          <div style={{ fontSize: '1.15rem', color: 'var(--text-secondary)', fontWeight: 600, lineHeight: 1.3 }}>
            {item.prompt_text_en}
          </div>
        )}
        {item.prompt_text_fr && (
          <div style={{ fontFamily: 'Fredoka', fontSize: '2rem', fontWeight: 700, color: 'var(--text-primary)', marginTop: '0.3rem', lineHeight: 1.2 }}>
            {item.prompt_text_fr}
          </div>
        )}
      </div>

      {/* Options */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', width: '100%', maxWidth: 700 }}>
        {(item.options || []).map((opt, i) => {
          const isCorrect = i === correctIdx;
          return (
            <div key={i} data-testid={`review-option-${i}`} style={{
              padding: '1rem 1.5rem', borderRadius: '1rem',
              background: isCorrect ? 'rgba(47,158,68,0.12)' : 'rgba(255,255,255,0.95)',
              border: isCorrect ? '3px solid var(--success)' : '2px solid rgba(0,0,0,0.06)',
              display: 'flex', alignItems: 'center', gap: '0.75rem',
              boxShadow: isCorrect ? '0 0 12px rgba(47,158,68,0.15)' : '0 2px 8px rgba(0,0,0,0.04)',
            }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 42, height: 42, borderRadius: '50%', flexShrink: 0,
                background: isCorrect ? 'var(--success)' : 'rgba(0,0,0,0.05)',
                color: isCorrect ? 'white' : 'var(--text-muted)',
                fontSize: '1.1rem', fontWeight: 700, fontFamily: 'Fredoka',
              }}>
                {isCorrect ? <CheckCircle size={22} /> : String.fromCharCode(65 + i)}
              </span>
              {opt.image && (
                <img src={process.env.REACT_APP_IMAGE_BASE_URL ? `${process.env.REACT_APP_IMAGE_BASE_URL}/${opt.image}` : `/assets/content/${opt.image}`} alt="" style={{
                  width: 60, height: 60, objectFit: 'contain', borderRadius: '0.5rem',
                }} onError={e => { e.target.style.display = 'none'; }} />
              )}
              <span style={{
                fontSize: '1.35rem', fontWeight: isCorrect ? 800 : 600,
                fontFamily: 'Fredoka', color: isCorrect ? 'var(--success)' : 'var(--text-primary)',
              }}>
                {opt.text || opt.label || ''}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   TEAM MCQ SLIDE — La Course / Le Duel
   ═══════════════════════════════════════════ */
function TeamMCQSlide({ slide, settings }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', width: '100%' }}>
      <TeamPanel item={slide.t1Item} teamName={settings.team1Name || 'Team 1'} teamColor="var(--team1)" />
      <TeamPanel item={slide.t2Item} teamName={settings.team2Name || 'Team 2'} teamColor="var(--team2)" />
    </div>
  );
}

function TeamPanel({ item, teamName, teamColor }) {
  if (!item) {
    return (
      <div style={{
        background: 'rgba(0,0,0,0.03)', borderRadius: '1.25rem', padding: '2rem',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: '2px dashed rgba(0,0,0,0.08)',
      }}>
        <span style={{ fontFamily: 'Fredoka', fontSize: '1.2rem', color: 'var(--text-muted)' }}>No question</span>
      </div>
    );
  }

  const correctIdx = (item.options || []).findIndex(o => o.correct);

  return (
    <div style={{
      background: 'rgba(255,255,255,0.95)', borderRadius: '1.25rem',
      border: `3px solid ${teamColor}`, overflow: 'hidden',
    }}>
      {/* Team header */}
      <div style={{
        background: teamColor, color: 'white', padding: '0.5rem 1rem',
        fontFamily: 'Fredoka', fontSize: '1.15rem', fontWeight: 800, textAlign: 'center',
      }}>{teamName}</div>

      <div style={{ padding: '1rem' }}>
        {/* Question */}
        <div style={{ marginBottom: '0.75rem', textAlign: 'center' }}>
          {item.prompt_image && (
            <img src={process.env.REACT_APP_IMAGE_BASE_URL ? `${process.env.REACT_APP_IMAGE_BASE_URL}/${item.prompt_image}` : `/assets/content/${item.prompt_image}`} alt="" style={{
              maxWidth: 180, maxHeight: 120, objectFit: 'contain', margin: '0 auto 0.5rem', display: 'block', borderRadius: '0.5rem',
            }} onError={e => { e.target.style.display = 'none'; }} />
          )}
          {item.prompt_text_en && (
            <div style={{ fontSize: '1rem', color: 'var(--text-secondary)', fontWeight: 600 }}>{item.prompt_text_en}</div>
          )}
          {item.prompt_text_fr && (
            <div style={{ fontFamily: 'Fredoka', fontSize: '1.6rem', fontWeight: 700, color: 'var(--text-primary)', marginTop: '0.2rem' }}>
              {item.prompt_text_fr}
            </div>
          )}
          {item.audio_text_fr && !item.prompt_text_fr && (
            <div style={{ fontFamily: 'Fredoka', fontSize: '1.3rem', fontWeight: 600, color: 'var(--text-primary)', fontStyle: 'italic' }}>
              "{item.audio_text_fr}"
            </div>
          )}
        </div>

        {/* Options */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          {(item.options || []).map((opt, i) => {
            const isCorrect = i === correctIdx;
            return (
              <div key={i} style={{
                padding: '0.6rem 1rem', borderRadius: '0.75rem',
                background: isCorrect ? 'rgba(47,158,68,0.12)' : 'rgba(0,0,0,0.02)',
                border: isCorrect ? '2px solid var(--success)' : '2px solid rgba(0,0,0,0.04)',
                display: 'flex', alignItems: 'center', gap: '0.5rem',
              }}>
                {isCorrect && <CheckCircle size={20} color="var(--success)" />}
                <span style={{
                  fontSize: '1.2rem', fontWeight: isCorrect ? 800 : 600,
                  fontFamily: 'Fredoka', color: isCorrect ? 'var(--success)' : 'var(--text-primary)',
                }}>
                  {opt.text}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   TEXT ANSWER SLIDE — L'Horloge Folle
   ═══════════════════════════════════════════ */
function TextAnswerSlide({ slide }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.25rem' }}>
      {/* Instruction */}
      <div style={{
        fontFamily: 'Fredoka', fontSize: '1.3rem', fontWeight: 600, color: 'var(--text-secondary)', textAlign: 'center',
      }}>
        {slide.instruction}
      </div>

      {/* Prompt (number, equation, or sentence) */}
      {slide.prompt && (
        <div style={{
          fontFamily: 'Fredoka', fontSize: slide.prompt.length > 30 ? '1.5rem' : '3.5rem',
          fontWeight: 700, color: 'var(--text-primary)',
          background: 'rgba(255,255,255,0.95)', padding: '0.75rem 2.5rem',
          borderRadius: '1.25rem', border: '2px solid rgba(76,110,245,0.15)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.06)', textAlign: 'center',
          maxWidth: '100%', lineHeight: 1.3,
        }}>
          {slide.prompt}
        </div>
      )}

      {/* Audio indicator */}
      {slide.audioText && !slide.prompt && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.6rem',
          background: 'rgba(121,80,242,0.06)', padding: '0.75rem 1.5rem',
          borderRadius: '1rem', border: '2px solid rgba(121,80,242,0.15)',
        }}>
          <Volume2 size={28} color="#7950F2" />
          <span style={{ fontFamily: 'Fredoka', fontSize: '1.3rem', fontWeight: 600, color: 'var(--text-primary)', fontStyle: 'italic' }}>
            "{slide.audioText}"
          </span>
        </div>
      )}

      {/* Correct answer — BIG, GREEN */}
      <div style={{
        background: 'rgba(47,158,68,0.1)', borderRadius: '1.25rem', padding: '1.25rem 3rem',
        border: '3px solid var(--success)', boxShadow: '0 0 20px rgba(47,158,68,0.1)',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: '0.85rem', color: 'var(--success)', fontWeight: 700, fontFamily: 'Fredoka', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.25rem' }}>
          Correct Answer
        </div>
        <div style={{
          fontFamily: 'Fredoka', fontSize: '2.5rem', fontWeight: 800,
          color: 'var(--success)', lineHeight: 1.2,
        }}>
          {slide.correctAnswer}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   CATEGORY SLIDE — L'Avalanche / Tri Express
   ═══════════════════════════════════════════ */
function CategorySlide({ slide }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
      {/* Title */}
      <div style={{
        fontFamily: 'Fredoka', fontSize: '2rem', fontWeight: 700,
        color: 'var(--primary)', textAlign: 'center',
      }}>
        {slide.title}
      </div>
      {slide.instruction && (
        <div style={{ fontFamily: 'Fredoka', fontSize: '1.1rem', color: 'var(--text-secondary)', fontWeight: 600 }}>
          {slide.instruction}
        </div>
      )}

      {/* Category labels */}
      {slide.categories && slide.categories.length > 0 && (
        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', justifyContent: 'center' }}>
          {slide.categories.map((cat, i) => (
            <span key={i} style={{
              padding: '0.4rem 1.25rem', borderRadius: '2rem',
              background: cat.color || '#4C6EF5', color: 'white',
              fontFamily: 'Fredoka', fontSize: '1.2rem', fontWeight: 700,
            }}>{cat.label}</span>
          ))}
        </div>
      )}

      {/* Words grid */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: '0.6rem', justifyContent: 'center',
        maxWidth: 850, padding: '1rem',
      }}>
        {(slide.words || []).map((w, i) => (
          <div key={i} style={{
            padding: '0.6rem 1.25rem', borderRadius: '1rem',
            background: w.isCorrect ? 'rgba(47,158,68,0.1)' : 'rgba(224,49,49,0.06)',
            border: `3px solid ${w.isCorrect ? 'var(--success)' : 'rgba(224,49,49,0.2)'}`,
            display: 'flex', alignItems: 'center', gap: '0.5rem',
          }}>
            {w.isCorrect ? <CheckCircle size={20} color="var(--success)" /> : <X size={18} color="#ccc" />}
            <span style={{
              fontFamily: 'Fredoka', fontSize: '1.4rem', fontWeight: 700,
              color: w.isCorrect ? 'var(--success)' : 'var(--text-muted)',
              textDecoration: w.isCorrect ? 'none' : 'line-through',
            }}>{w.text}</span>
            {w.correctCategory && (
              <span style={{
                fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)',
                background: 'rgba(0,0,0,0.04)', padding: '0.1rem 0.4rem', borderRadius: '0.3rem',
              }}>{w.correctCategory}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   PAIRS SLIDE — La Memoire
   ═══════════════════════════════════════════ */
function PairsSlide({ slide }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
      <div style={{
        fontFamily: 'Fredoka', fontSize: '2rem', fontWeight: 700,
        color: 'var(--primary)', textAlign: 'center',
      }}>
        {slide.title}
      </div>
      <span style={{
        padding: '0.2rem 0.75rem', borderRadius: '0.5rem',
        background: 'rgba(76,110,245,0.08)', fontFamily: 'Fredoka',
        fontSize: '1rem', fontWeight: 700, color: '#4C6EF5',
      }}>{slide.matchType}</span>

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
        gap: '0.75rem', width: '100%', maxWidth: 850,
      }}>
        {(slide.pairs || []).map((p, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center',
            background: 'rgba(47,158,68,0.06)', borderRadius: '1rem',
            padding: '0.85rem 1.25rem', border: '2px solid rgba(47,158,68,0.15)',
            gap: '0.75rem',
          }}>
            <span style={{
              fontFamily: 'Fredoka', fontSize: '1.3rem', fontWeight: 700,
              color: '#4C6EF5', flex: 1,
            }}>{p.a}</span>
            <span style={{
              width: 32, height: 32, borderRadius: '50%',
              background: 'var(--success)', color: 'white',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, fontSize: '1rem', fontWeight: 700,
            }}>=</span>
            <span style={{
              fontFamily: 'Fredoka', fontSize: '1.3rem', fontWeight: 700,
              color: 'var(--success)', flex: 1, textAlign: 'right',
            }}>{p.b}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   SENTENCE SLIDE — Batisseur
   ═══════════════════════════════════════════ */
function SentenceSlide({ slide }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.25rem' }}>
      {/* English meaning */}
      <div style={{
        fontFamily: 'Fredoka', fontSize: '1.8rem', fontWeight: 700,
        color: 'var(--text-primary)', textAlign: 'center',
        background: 'rgba(255,255,255,0.95)', padding: '1rem 2rem',
        borderRadius: '1.25rem', border: '2px solid rgba(245,159,0,0.2)',
        boxShadow: '0 4px 16px rgba(0,0,0,0.06)', maxWidth: 800,
      }}>
        {slide.english}
      </div>

      {/* Hint */}
      {slide.hint && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <Lightbulb size={18} color="#F59F00" />
          <span style={{ fontFamily: 'Nunito', fontSize: '1rem', color: '#888', fontWeight: 600, fontStyle: 'italic' }}>
            {slide.hint}
          </span>
        </div>
      )}

      {/* Correct word order — numbered blocks */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: '0.5rem', justifyContent: 'center',
        maxWidth: 850,
      }}>
        {(slide.correctOrder || []).map((word, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: '0.3rem',
          }}>
            <span style={{
              width: 28, height: 28, borderRadius: '50%',
              background: 'var(--success)', color: 'white',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: 'Fredoka', fontSize: '0.85rem', fontWeight: 700, flexShrink: 0,
            }}>{i + 1}</span>
            <span style={{
              padding: '0.6rem 1.25rem', borderRadius: '0.85rem',
              background: 'rgba(47,158,68,0.1)', border: '2px solid var(--success)',
              fontFamily: 'Fredoka', fontSize: '1.6rem', fontWeight: 700,
              color: 'var(--success)',
            }}>{word}</span>
          </div>
        ))}
      </div>

      {/* Completed sentence */}
      {slide.completedSentence && (
        <div style={{
          fontFamily: 'Fredoka', fontSize: '1.4rem', fontWeight: 600,
          color: 'var(--text-secondary)', fontStyle: 'italic', textAlign: 'center',
          maxWidth: 700,
        }}>
          {slide.completedSentence}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════
   PASSAGE SLIDE — Robot Balayeur
   ═══════════════════════════════════════════ */
function PassageSlide({ slide }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
      <div style={{
        fontFamily: 'Fredoka', fontSize: '2rem', fontWeight: 700,
        color: 'var(--primary)', textAlign: 'center',
      }}>
        {slide.title}
      </div>
      {slide.contentType && (
        <span style={{
          padding: '0.15rem 0.6rem', borderRadius: '2rem',
          background: 'rgba(76,110,245,0.08)', fontFamily: 'Fredoka',
          fontSize: '0.85rem', fontWeight: 700, color: '#4C6EF5',
        }}>{slide.contentType}</span>
      )}

      {/* Words displayed like the reading game */}
      <div style={{
        background: 'rgba(255,255,255,0.95)', borderRadius: '1.5rem',
        padding: '2rem 3rem', maxWidth: 900, width: '100%',
        border: '2px solid rgba(0,0,0,0.06)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
      }}>
        <div style={{
          fontFamily: 'Fredoka', fontSize: '2rem', fontWeight: 700,
          color: 'var(--text-primary)', lineHeight: 1.8, textAlign: 'center',
        }}>
          {(slide.words || []).join(' ')}
        </div>
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════
   RECIPE STEP SLIDE — Le Chef
   Shows one recipe step per slide with correct action highlighted
   ═══════════════════════════════════════════ */
function RecipeStepSlide({ slide }) {
  const allOptions = [slide.correctAction, ...slide.distractors];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.25rem' }}>
      {/* Recipe header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.6rem',
        background: 'rgba(232,89,12,0.06)', padding: '0.6rem 1.5rem',
        borderRadius: '1rem', border: '2px solid rgba(232,89,12,0.15)',
      }}>
        <ChefHat size={24} color="#E8590C" />
        <span style={{ fontFamily: 'Fredoka', fontSize: '1.4rem', fontWeight: 700, color: '#E8590C' }}>
          {slide.recipeName}
        </span>
        {slide.recipeNameEn && (
          <span style={{ fontFamily: 'Nunito', fontSize: '1rem', color: '#888', fontStyle: 'italic' }}>
            ({slide.recipeNameEn})
          </span>
        )}
        <span style={{
          padding: '0.15rem 0.6rem', borderRadius: '2rem',
          background: 'rgba(0,0,0,0.05)', fontFamily: 'Fredoka',
          fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-muted)',
        }}>
          Recipe {slide.recipeNum}/{slide.totalRecipes}
        </span>
      </div>

      {/* Cultural origin */}
      {slide.culturalOrigin && (
        <div style={{
          fontFamily: 'Nunito', fontSize: '1rem', color: '#888',
          fontStyle: 'italic', textAlign: 'center', maxWidth: 600,
        }}>
          {slide.culturalOrigin}
        </div>
      )}

      {/* Step indicator */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.5rem',
      }}>
        {Array.from({ length: slide.totalSteps }).map((_, i) => (
          <div key={i} style={{
            width: i + 1 === slide.stepNum ? 28 : 12, height: 12, borderRadius: 6,
            background: i + 1 < slide.stepNum ? '#2F9E44' : i + 1 === slide.stepNum ? '#E8590C' : 'rgba(0,0,0,0.1)',
            transition: 'all 0.3s',
          }} />
        ))}
        <span style={{ fontFamily: 'Fredoka', fontSize: '0.95rem', color: 'var(--text-muted)', marginLeft: '0.5rem' }}>
          Step {slide.stepNum} of {slide.totalSteps}
        </span>
      </div>

      {/* French instruction — large */}
      <div style={{
        background: 'rgba(255,255,255,0.95)', borderRadius: '1.5rem', padding: '1.5rem 2.5rem',
        border: '2px solid rgba(232,89,12,0.12)', width: '100%', maxWidth: 750,
        boxShadow: '0 4px 20px rgba(0,0,0,0.06)', textAlign: 'center',
      }}>
        <div style={{
          fontFamily: 'Fredoka', fontSize: '2rem', fontWeight: 700,
          color: 'var(--text-primary)', lineHeight: 1.3,
        }}>
          {slide.instructionFr}
        </div>
        {slide.instructionEn && (
          <div style={{
            fontFamily: 'Nunito', fontSize: '1.1rem', color: '#999',
            fontStyle: 'italic', marginTop: '0.4rem',
          }}>
            {slide.instructionEn}
          </div>
        )}
      </div>

      {/* Options — correct one highlighted */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', width: '100%', maxWidth: 700 }}>
        {allOptions.map((option, i) => {
          const isCorrect = option === slide.correctAction;
          return (
            <div key={i} style={{
              padding: '1rem 1.5rem', borderRadius: '1rem',
              background: isCorrect ? 'rgba(47,158,68,0.12)' : 'rgba(255,255,255,0.95)',
              border: isCorrect ? '3px solid var(--success)' : '2px solid rgba(0,0,0,0.06)',
              display: 'flex', alignItems: 'center', gap: '0.75rem',
              boxShadow: isCorrect ? '0 0 12px rgba(47,158,68,0.15)' : '0 2px 8px rgba(0,0,0,0.04)',
            }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 42, height: 42, borderRadius: '50%', flexShrink: 0,
                background: isCorrect ? 'var(--success)' : 'rgba(0,0,0,0.05)',
                color: isCorrect ? 'white' : 'var(--text-muted)',
                fontSize: '1.1rem', fontWeight: 700, fontFamily: 'Fredoka',
              }}>
                {isCorrect ? <CheckCircle size={22} /> : String.fromCharCode(65 + i)}
              </span>
              <span style={{
                fontSize: '1.35rem', fontWeight: isCorrect ? 800 : 600,
                fontFamily: 'Fredoka', color: isCorrect ? 'var(--success)' : 'var(--text-primary)',
              }}>
                {option}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
